import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useCreateStage, useUpdateStage } from "@/lib/queries";
import {
  EXIT_SCOPES, NEW_STAGE, STAGE_DESCRIPTION_MAX, STAGE_GROUPS, crossingProblem, exitScopeText,
  groupText, newStagePayload, slugFromLabel, stageChanges, stageProblems, stageValues,
  type StageGroupName, type StageValues,
} from "@/lib/stages";
import type { StageRow } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Add a stage to the tenant's catalog, or edit one (ADR-077).
 *
 * The name a stage is stored by is typed once, on create, and suggested from its label: outcomes,
 * line caller rules, voicemail messages and every lead's history name the stage by it, so it is
 * not renamed afterwards; the label is. One of the platform's four (New, Do not call, Signed,
 * Signed pending intake) takes a new label and description only. A stage with leads in it cannot
 * move into or out of the exits: that would take leads off dial lists, or put them back, behind the
 * admin's back — an outcome or a reopen does that, lead by lead.
 */
export function StageFormDialog({
  open, onOpenChange, stage, catalog, tenantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  stage?: StageRow;
  /** The tenant's stages, for the names already taken. */
  catalog: readonly StageRow[];
  /** A superadmin's tenant; null for everyone else, whose own it is. */
  tenantId: number | null;
}) {
  const toast = useToast();
  const create = useCreateStage();
  const update = useUpdateStage();
  const [values, setValues] = React.useState<StageValues>(NEW_STAGE);
  const [initial, setInitial] = React.useState<StageValues>(NEW_STAGE);
  /** Whether the slug still follows the label: until somebody types in it. */
  const [slugFollows, setSlugFollows] = React.useState(true);
  const [tried, setTried] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const start = stage ? stageValues(stage) : NEW_STAGE;
    setValues(start);
    setInitial(start);
    setSlugFollows(!stage);
    setTried(false);
    setFormError(null);
  }, [open, stage]);

  const set = <K extends keyof StageValues>(key: K, value: StageValues[K]) =>
    setValues((v) => {
      const next = { ...v, [key]: value };
      if (key === "label" && slugFollows && !stage) next.slug = slugFromLabel(String(value));
      return next;
    });

  const creating = !stage;
  const system = Boolean(stage?.system);
  const problems = stageProblems(values, catalog.map((s) => s.slug), creating);
  const crossing = stage ? crossingProblem(stage, values.group) : null;
  const changed = JSON.stringify(values) !== JSON.stringify(initial);
  const exit = values.group === "exit";
  const pending = create.isPending || update.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    setFormError(null);
    if (Object.keys(problems).length > 0 || crossing) return;
    try {
      if (stage) {
        const changes = stageChanges(values, stage);
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({ slug: stage.slug, tenantId, ...changes });
        }
        toast.success("Stage saved", values.label.trim());
      } else {
        await create.mutateAsync({ ...newStagePayload(values), tenant_id: tenantId });
        toast.success("Stage added", values.label.trim());
      }
      onOpenChange(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Could not save the stage.");
    }
  };

  const shown = (key: keyof StageValues) => (tried ? problems[key] : undefined);
  // The stored name is checked as it is typed: it is the one field that cannot be fixed later.
  const slugError = creating && (values.slug || tried) ? problems.slug : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{stage ? "Edit stage" : "New stage"}</DialogTitle>
          <DialogDescription>
            {stage
              ? system
                ? "One of the platform's four: its name and description can change, not what it does."
                : "What it is called, and what it does to a lead in it."
              : "A step in the lifecycle a contact moves through. Outcomes put leads in it, and the Contacts page counts them."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={(e) => void submit(e)} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" htmlFor="stage-label" required error={shown("label")}>
                <Input
                  autoComplete="off"
                  placeholder="Retainer sent"
                  invalid={Boolean(shown("label"))}
                  value={values.label}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("label", e.target.value)}
                />
              </Field>
              <Field
                label="Stored as"
                htmlFor="stage-slug"
                required={creating}
                error={slugError}
                hint={
                  creating
                    ? "What outcomes, caller rules and the history store. It cannot be changed later."
                    : "Fixed. Outcomes, caller rules and the history name the stage by it."
                }
              >
                <Input
                  // Read-only rather than disabled, as on the other catalogs' forms.
                  readOnly={!creating}
                  className={cn("font-mono", !creating && "bg-surface-sunken text-text-muted")}
                  autoComplete="off"
                  placeholder="retainer_sent"
                  invalid={Boolean(slugError)}
                  value={values.slug}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSlugFollows(false);
                    set("slug", e.target.value);
                  }}
                />
              </Field>
            </div>

            <Field
              label="Description"
              htmlFor="stage-description"
              error={shown("description")}
              hint={`What a lead in it is waiting for. Shown beside the stage wherever it is picked; at most ${STAGE_DESCRIPTION_MAX} characters.`}
            >
              <Textarea
                rows={2}
                className="min-h-16 font-sans text-sm"
                invalid={Boolean(shown("description"))}
                value={values.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </Field>

            <Field
              label="Group"
              htmlFor="stage-group"
              hint={system ? "Fixed for the platform's own stages." : groupText(values.group).description}
            >
              <Select
                value={values.group}
                disabled={system}
                onValueChange={(v) => set("group", v as StageGroupName)}
              >
                <SelectTrigger id="stage-group" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_GROUPS.map((g) => (
                    <SelectItem key={g} value={g} description={groupText(g).description}>
                      {groupText(g).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {crossing && <Callout tone="warning">{crossing}</Callout>}

            {exit ? (
              <Field
                label="Who else leaves with it"
                htmlFor="stage-reach"
                hint={
                  system
                    ? "Fixed for the platform's own stages."
                    : exitScopeText(values.exit_scope)?.description
                }
              >
                <Select
                  value={values.exit_scope}
                  disabled={system}
                  onValueChange={(v) => set("exit_scope", v as StageValues["exit_scope"])}
                >
                  <SelectTrigger id="stage-reach" className="sm:w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXIT_SCOPES.map((scope) => (
                      <SelectItem key={scope} value={scope} description={exitScopeText(scope)?.description}>
                        {exitScopeText(scope)?.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ) : (
              <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                <div>
                  <p id="stage-boost-label" className="text-xs font-medium text-text">
                    Dial near the top
                  </p>
                  <p className="text-xs text-text-muted">
                    A campaign's cadence ranks leads in this stage in its "near signing" band, as it does
                    About to sign: ahead of new leads and mid-cycle calls, behind callbacks.
                  </p>
                </div>
                <Switch
                  checked={values.dial_boost}
                  disabled={system}
                  onCheckedChange={(checked) => set("dial_boost", checked)}
                  aria-labelledby="stage-boost-label"
                />
              </div>
            )}
            {exit && !system && (
              <p className="text-xs text-text-muted">
                A lead in an exit leaves every campaign's dial list, and only another exit, or an admin's
                Reopen on the contact, moves it on.
              </p>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={pending} disabled={Boolean(crossing)}>
              {stage ? "Save changes" : "Add stage"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
